import React, { memo } from 'react';

import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import UnsavedChangesDialog from 'components/Transactions/UnsavedChangesDialog';

import type { TransactionEditViewModel } from './TransactionEditViewModel';
import WorkflowCoordinator from './recurring/WorkflowCoordinator';
import TransactionEditFormContent from './TransactionEditFormContent';
import TransactionEditSurface from './TransactionEditSurface';

type Props = {
  rootModalDrawer?: RootModalDrawerOptions;
  viewModel: TransactionEditViewModel;
};

function TransactionEditDialogView({ rootModalDrawer: rootModalDrawerProp, viewModel }: Props): React.JSX.Element {
  const {
    actions: {
      closeAll,
      discardAndMarkRecurring,
      exitUnsavedDialog,
      onSave,
      showUnsavedDialog,
      stopRecurring,
      updateAndMarkRecurring,
    },
    derived: {
      completeEnough,
      dialogOpen,
      open,
      rootModalDrawer: derivedRootModalDrawer,
      showRecurringOptions,
      showUnsavedRecurringPrompt,
      showUnsavedSplitsChanges,
      splitHasUnsavedChanges,
      title,
      waiting,
    },
    values: { create },
  } = viewModel;
  const rootModalDrawer = rootModalDrawerProp ?? derivedRootModalDrawer ?? undefined;
  const showRecurringOptionsInline = Boolean(showRecurringOptions && !rootModalDrawer);

  // A nested options drawer supplies its own contextual title. Keeping the
  // underlying title stable avoids two competing titles while drawers overlap.
  const shellTitle = showRecurringOptions && rootModalDrawer ? 'Transaction detail' : title;

  return (
    <>
      <TransactionEditSurface
        isDrawer={Boolean(rootModalDrawer)}
        onBack={stopRecurring}
        onClose={splitHasUnsavedChanges ? showUnsavedDialog : closeAll}
        open={dialogOpen}
        showBackButton={showRecurringOptionsInline}
        title={shellTitle}
      >
        <TransactionEditFormContent
          rootModalDrawer={rootModalDrawer}
          showRecurringOptionsInline={showRecurringOptionsInline}
          viewModel={viewModel}
        />
      </TransactionEditSurface>

      {showUnsavedSplitsChanges && (
        <UnsavedChangesDialog
          onClose={exitUnsavedDialog}
          open={open && showUnsavedSplitsChanges && !waiting}
          closeAll={showUnsavedRecurringPrompt ? discardAndMarkRecurring : closeAll}
          update={showUnsavedRecurringPrompt ? updateAndMarkRecurring : onSave}
          notComplete={!completeEnough}
          create={create}
          continueAfterDiscard={showUnsavedRecurringPrompt}
          continueAfterUpdate={showUnsavedRecurringPrompt}
        />
      )}

      <WorkflowCoordinator rootModalDrawer={rootModalDrawer} viewModel={viewModel} />
    </>
  );
}

export default memo(TransactionEditDialogView);
